import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const KERNEL_CSRF_HEADER = "x-d8-csrf-token";
const KERNEL_OPERATOR_ID =
    process.env.REACT_APP_KERNEL_OPERATOR_ID || "local-operator";
const DEFAULT_PERMIT_REASON = "frontend governed mutation";

export const api = axios.create({
    baseURL: API,
    timeout: 120000,
    withCredentials: true,
});

// ============================================================
// D8-K permit acquisition helpers
//
// The backend remains the authority for login/session, CSRF, review
// creation, approval, and permit issuance. The frontend may initiate the
// flow and forward the server-issued permit_id to the governed mutation.
// No policy decision is made client-side.
// ============================================================

export const kernelLogin = (actor, role) =>
    api
        .post("/internal/login", { actor_id: actor, actor_role: role })
        .then((r) => r.data);

export const kernelCreateReview = (method, path, payload, csrfToken) =>
    api
        .post(
            "/kernel/reviews",
            {
                method: String(method).toUpperCase(),
                path,
                payload,
                reason: DEFAULT_PERMIT_REASON,
            },
            { headers: { [KERNEL_CSRF_HEADER]: csrfToken } },
        )
        .then((r) => r.data);

export const kernelApproveReview = (reviewId, csrfToken) =>
    api
        .post(
            `/kernel/reviews/${reviewId}/approve`,
            {},
            { headers: { [KERNEL_CSRF_HEADER]: csrfToken } },
        )
        .then((r) => r.data);

export const withPermit = async (method, path, payload, mutateFn, options = {}) => {
    const role = options.role || "reviewer";
    const actor = options.actor || KERNEL_OPERATOR_ID;
    const session = await kernelLogin(actor, role);
    const csrfToken = session.csrf_token;
    const review = await kernelCreateReview(method, path, payload, csrfToken);
    const approval = await kernelApproveReview(review.review_id, csrfToken);
    const permitId = approval.permit.permit_id;
    return mutateFn(permitId);
};

// Clips
export const generateClips = (payload) => api.post("/clips/generate", payload).then((r) => r.data);
export const listClips = (params) => api.get("/clips", { params }).then((r) => r.data);
export const getClip = (id) => api.get(`/clips/${id}`).then((r) => r.data);
export const saveClip = (id, saved) => api.post(`/clips/${id}/save`, { saved }).then((r) => r.data);
export const deleteClip = (id) =>
    withPermit("DELETE", `/api/clips/${id}`, { clip_id: id }, (permit_id) =>
        api.delete(`/clips/${id}`, { params: { permit_id } }).then((r) => r.data),
    );
export const rewriteClip = (id) => api.post(`/clips/${id}/rewrite`).then((r) => r.data);
export const classifyClip = (id) => api.post(`/clips/${id}/classify`).then((r) => r.data);

// Analytics
export const getDashboard = (params) => api.get("/analytics/dashboard", { params }).then((r) => r.data);
export const getAnalytics = () => api.get("/analytics/overview").then((r) => r.data);
export const ingestEvent = (evt) => api.post("/analytics/ingest", evt).then((r) => r.data);
export const ingestBatch = (events) => api.post("/analytics/ingest/batch", { events }).then((r) => r.data);
export const listEvents = (params) => api.get("/analytics/events", { params }).then((r) => r.data);

// Channels
export const listChannels = () => api.get("/channels").then((r) => r.data);
export const createChannel = (c) => api.post("/channels", c).then((r) => r.data);
export const updateChannel = (id, c) => api.put(`/channels/${id}`, c).then((r) => r.data);
export const deleteChannel = (id) =>
    withPermit("DELETE", `/api/channels/${id}`, { channel_id: id }, (permit_id) =>
        api.delete(`/channels/${id}`, { params: { permit_id } }).then((r) => r.data),
    );

// Campaigns
export const listCampaigns = () => api.get("/campaigns").then((r) => r.data);
export const createCampaign = (c) => api.post("/campaigns", c).then((r) => r.data);
export const updateCampaign = (id, c) => api.put(`/campaigns/${id}`, c).then((r) => r.data);
export const deleteCampaign = (id) =>
    withPermit("DELETE", `/api/campaigns/${id}`, { campaign_id: id }, (permit_id) =>
        api.delete(`/campaigns/${id}`, { params: { permit_id } }).then((r) => r.data),
    );

// Settings
export const getSettings = () => api.get("/settings").then((r) => r.data);
export const updateSettings = (s) =>
    withPermit("PUT", "/api/settings", s, (permit_id) =>
        api.put("/settings", s, { params: { permit_id } }).then((r) => r.data),
    );

// Admin
export const runCleanup = () =>
    withPermit(
        "POST",
        "/api/admin/cleanup",
        {},
        (permit_id) =>
            api.post("/admin/cleanup", null, { params: { permit_id } }).then((r) => r.data),
        { role: "admin" },
    );

// Routing
export const getBestChannel = (clipId) => api.get(`/clips/${clipId}/best-channel`).then((r) => r.data);
export const getRoutingSummary = () => api.get("/analytics/routing-summary").then((r) => r.data);

// ============================================================
// Dark8-s Governed Workflow (Operator Dashboard)
// No publishing authority is created by any of these calls.
// publish_allowed remains false at all layers.
// ============================================================

// Concept intake — POST /api/dark8s/concepts
export const ingestConcept = (payload) =>
    api.post("/dark8s/concepts", payload).then((r) => r.data);

// Review queue — GET /api/dark8s/concepts/review-queue
export const getReviewQueue = (status) =>
    api
        .get("/dark8s/concepts/review-queue", status ? { params: { status } } : {})
        .then((r) => r.data);

// All concepts for a given status (used by home summary)
export const getConceptsByStatus = (status) =>
    api.get("/dark8s/concepts/review-queue", { params: { status } }).then((r) => r.data);

// Review transition — POST /api/dark8s/concepts/{id}/review
export const reviewConcept = (conceptId, status, operatorNote) =>
    api
        .post(`/dark8s/concepts/${conceptId}/review`, { status, operator_note: operatorNote })
        .then((r) => r.data);

// Script draft — POST /api/dark8s/concepts/{id}/draft
export const prepareDraft = (conceptId) =>
    api.post(`/dark8s/concepts/${conceptId}/draft`).then((r) => r.data);

// Manual export package — GET /api/dark8s/concepts/{id}/export
export const exportConcept = (conceptId) =>
    api.get(`/dark8s/concepts/${conceptId}/export`).then((r) => r.data);

// ============================================================
// Phase 18 — Asset Generation Sandbox
// No publishing authority is created by any of these calls.
// publish_allowed remains false at all layers.
// ============================================================

// Asset package — POST /api/dark8s/concepts/{id}/asset-package
export const prepareAssetPackage = (conceptId) =>
    api.post(`/dark8s/concepts/${conceptId}/asset-package`).then((r) => r.data);

// Asset review — POST /api/dark8s/concepts/{id}/asset-review
export const reviewAssetPackage = (conceptId, status, operatorNote) =>
    api
        .post(`/dark8s/concepts/${conceptId}/asset-review`, { status, operator_note: operatorNote })
        .then((r) => r.data);

// Production export — GET /api/dark8s/concepts/{id}/production-export
export const productionExport = (conceptId) =>
    api.get(`/dark8s/concepts/${conceptId}/production-export`).then((r) => r.data);

// ============================================================
// Phase 19 — Operator Identity (RBAC)
// Returns current operator identity and roles.
// publish_allowed is always false. No publishing authority is granted.
// ============================================================

// Operator identity — GET /api/operator/me
export const getOperatorMe = () =>
    api.get("/operator/me").then((r) => r.data).catch(() => null);
